import { ref, onMounted, onUnmounted } from 'vue'
import { logger } from '@/core/utils/logger'

interface VersionInfo {
  version: string
  buildTime: string
}

/**
 * Composable for checking if a new app version is available
 * Polls /version.json every 5 minutes to detect new deployments
 */
/**
 * Fetch version info from /version.json
 */
async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
      }
    })

    if (!response.ok) {
      logger.warn('Failed to fetch version.json:', response.status)
      return null
    }

    return await response.json()
  } catch (error) {
    logger.warn('Error fetching version:', error)
    return null
  }
}

/**
 * Reload the page to get the new version
 */
function reloadPage() {
  window.location.reload()
}

export function useVersionCheck() {
  const hasNewVersion = ref(false)
  const currentVersion = ref<string | null>(null)
  let intervalId: number | null = null

  /**
   * Check if a new version is available
   */
  async function checkVersion() {
    const versionInfo = await fetchVersion()

    if (!versionInfo) {
      return
    }

    // First time - store the current version
    if (currentVersion.value === null) {
      currentVersion.value = versionInfo.version
      logger.log('Initial version:', currentVersion.value)
      return
    }

    // Check if version has changed
    if (versionInfo.version !== currentVersion.value) {
      logger.log('New version detected!', {
        current: currentVersion.value,
        new: versionInfo.version
      })
      hasNewVersion.value = true
    }
  }

  /**
   * Start version checking
   */
  function startVersionCheck() {
    // Skip version checking in development mode (version.json only exists in production builds)
    if (import.meta.env.DEV) {
      return
    }

    // Check immediately on mount
    checkVersion()

    // Then check every 5 minutes
    intervalId = window.setInterval(() => {
      checkVersion()
    }, 5 * 60 * 1000) // 5 minutes
  }

  /**
   * Stop version checking
   */
  function stopVersionCheck() {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  // Auto-start on mount, auto-stop on unmount
  onMounted(() => {
    startVersionCheck()
  })

  onUnmounted(() => {
    stopVersionCheck()
  })

  return {
    hasNewVersion,
    reloadPage
  }
}
